import VerticalStepper from 'app/views/components/stepper-component'
import PrimaryButton from 'app/views/components/buttons/PrimaryButton'
import UpdatedDealsModal, {
  ChildDealRef,
} from 'app/views/pages/deals-management/deals/updated-deal-form'
import { useRef, useState } from 'react'
import DealManageFees, {
  ChildDealManageFeesRef,
} from 'app/views/components/Fees/dealFees'
import DealPricingGridComponent, { ChildDealManagePricingGridRef } from 'app/views/components/deal-pricing-grid/deal-pricing-grid'
import DealAssignabilityComponent, {
  ChildDealManageAssignabilityRef,
} from 'app/views/components/deal-assignability/deal-assignability'
import CreditPreambleComponent, { ChildCreditAgreementPreambleRef } from 'app/views/components/deal-credit-agreement-preamble/deal-credit-agreement-preamble'

type Props = {
  onClose: () => void
  deal?: any
  dealAmendment?: any
  step?: number
  amendment?: string | null
}

interface CustomStepContentProps {
  onNext: () => void
  onPrevious: () => void
  onClose: () => void
}

const DealsFormWithoutStepper: React.FC<Props> = ({
  deal,
  amendment,
  dealAmendment,
  onClose,
  step,
}) => {
  const [disabledSteps, setDisabledSteps] = useState<any>()
  const [upd_deal, setDeal] = useState(deal)
  const [upd_amendment, setAmendment] = useState(dealAmendment)

  const DealContent: React.FC<CustomStepContentProps> = ({ onNext }) => {
    const childRef = useRef<ChildDealRef>(null)
    const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true)
    const [loading, setLoading] = useState(false);

    const handleSaveDealAndExit = async () => {
      if (childRef.current) {
        setLoading(true)
        await childRef.current.handleClick()
        setLoading(false)
        onClose()
      }
    }

    const handleSaveDealAndNext = async () => {
      if (childRef.current) {
        setLoading(true)
        await childRef.current.handleClick()
        setLoading(false)
        onNext()
      }
    }

    return (
      <div className="create-new">
        <UpdatedDealsModal
          deal={upd_deal}
          amendment={amendment}
          dealAmendment={upd_amendment}
          ref={childRef}
          setSaveButtonDisabled={setIsSaveButtonDisabled}
          setDeal={setDeal}
          setAmendment={setAmendment}
          setDisabledSteps={setDisabledSteps}
          onClose={onClose}
        />{' '}
        <div
          className="btn-group"
          style={{
            marginTop: 50,
            textAlign: 'right',
            display: 'flex',
            gap: '10px',
          }}
        >
          <PrimaryButton
            onClick={() => handleSaveDealAndExit()}
            className="form-button"
            w="33%"
            loading={loading}
            disabled={isSaveButtonDisabled}
          >
            Save and Exit
          </PrimaryButton>
          <PrimaryButton
            onClick={() => handleSaveDealAndNext()}
            className="form-button"
            w="33%"
            loading={loading}
            disabled={isSaveButtonDisabled}
          >
            Save and Proceed
          </PrimaryButton>
          <PrimaryButton
            onClick={() => onClose()}
            className="form-button"
            loading={loading}
            w="33%"
          >
            Exit
          </PrimaryButton>
        </div>
      </div>
    )
  }

  const FeesContent: React.FC<CustomStepContentProps> = ({
    onNext,
    onPrevious,
    onClose,
  }) => {
    const childRef = useRef<ChildDealManageFeesRef>(null)
    const [loading, setLoading] = useState(false);

    const hasPricingGrid = upd_deal?.isPricingGrid || upd_amendment?.isPricingGrid

    const handleSaveDealAndExit = async () => {
      if (childRef.current) {
        setLoading(true)
        await childRef.current.handleClick()
        setLoading(false)
        onClose()
      }
    }

    const handleSaveDealAndNext = async () => {
      if (childRef.current) {
        setLoading(true)
        await childRef.current.handleClick()
        setLoading(false)
        if (!hasPricingGrid) {
          // Call onNext twice to skip Pricing Grid
          onNext()
          onNext()
        } else {
          onNext()
        }
      }
    }


    const handlePrevious = () => {
      if (typeof onNext === 'function') {
        onPrevious()
      }
    }

    return (
      <div className="create-new">
        <DealManageFees
          deal={upd_deal}
          amendment={amendment}
          dealAmendment={upd_amendment}
          setAmendment={setAmendment}
          setDeal={setDeal}
          ref={childRef}
        />{' '}
        <div
          className="btn-group"
          style={{ marginTop: 10, display: 'flex', gap: '10px' }}
        >
          <PrimaryButton
            onClick={() => handleSaveDealAndExit()}
            className="form-button"
            w="33.3%"
            loading={loading}
          >
            Save and Exit
          </PrimaryButton>
          <PrimaryButton
            onClick={handleSaveDealAndNext}
            className="form-button"
            w="33%"
            loading={loading}
          >
            Save and Proceed
          </PrimaryButton>

          <PrimaryButton
            onClick={() => handlePrevious()}
            className="form-button"
            w="33.3%"
            loading={loading}
          >
            Previous
          </PrimaryButton>
          <PrimaryButton
            onClick={() => onClose()}
            className="form-button"
            loading={loading}
            w="33.3%"
          >
            Close
          </PrimaryButton>
        </div>
      </div>
    )
  }

  const PricingGridContent: React.FC<CustomStepContentProps> = ({
    onNext,
    onPrevious,
    onClose,
  }) => {
    const childRef = useRef<ChildDealManagePricingGridRef>(null)
    const [loading, setLoading] = useState(false);

    const handleSaveDealAndExit = async () => {
      if (childRef.current) {
        setLoading(true)
        await childRef.current.handleClick()
        setLoading(false)
        onClose()
      }
    }

    const handleSaveDealAndNext = async () => {
      if (childRef.current) {
        setLoading(true)
        await childRef.current.handleClick()
        setLoading(false)
        onNext()
      }
    }

    const handlePrevious = () => {
      if (typeof onNext === 'function') {
        onPrevious()
      }
    }

    return (
      <div className="create-new">
        <DealPricingGridComponent
          deal={upd_deal}
          amendment={amendment}
          dealAmendment={upd_amendment}
          setAmendment={setAmendment}
          setDeal={setDeal}
          ref={childRef}
          setDisabledSteps={disabledSteps}
        />{' '}
        <div
          className="btn-group"
          style={{ marginTop: 10, display: 'flex', gap: '10px' }}
        >
          <PrimaryButton
            onClick={() => handleSaveDealAndExit()}
            className="form-button"
            w="33.3%"
            loading={loading}
          >
            Save and Exit
          </PrimaryButton>
          <PrimaryButton
            onClick={handleSaveDealAndNext}
            className="form-button"
            w="33%"
            loading={loading}
          >
            Save and Proceed
          </PrimaryButton>
          <PrimaryButton
            onClick={() => handlePrevious()}
            className="form-button"
            loading={loading}
            w="33.3%"
          >
            Previous
          </PrimaryButton>
          <PrimaryButton
            onClick={() => onClose()}
            className="form-button"
            loading={loading}
            w="33.3%"
          >
            Close
          </PrimaryButton>
        </div>
      </div>
    )
  }

  const DealAssignabilityContent: React.FC<CustomStepContentProps> = ({
    onNext,
    onPrevious,
    onClose,
  }) => {
    const childRef = useRef<ChildDealManageAssignabilityRef>(null)
    const [loading, setLoading] = useState(false)

    void onNext // Prevent unused variable warning, this is currently last step in stepper so no need for onNext

    const handleSaveDealAndExit = async () => {
      if (childRef.current) {
        setLoading(true)
        await childRef.current.handleClick()
        setLoading(false)
        onClose()
      }
    }

    const handleSaveDealAndNext = async () => {
      if (childRef.current) {
        setLoading(true)
        await childRef.current.handleClick()
        setLoading(false)
        onNext()
      }
    }

    const handlePrevious = () => {
      if (typeof onNext === 'function') {
        onPrevious()
      }
    }
    return (
      <div className="create-new">
        <DealAssignabilityComponent
          deal={upd_deal}
          amendment={amendment}
          dealAmendment={upd_amendment}
          setAmendment={setAmendment}
          setDeal={setDeal}
          ref={childRef}
        />
        <div
          className="btn-group"
          style={{ marginTop: 10, display: 'flex', gap: '10px' }}
        >
          <PrimaryButton
            onClick={() => handleSaveDealAndExit()}
            className="form-button"
            w="33.3%"
            loading={loading}
          >
            Save and Exit
          </PrimaryButton>
          <PrimaryButton
            onClick={handleSaveDealAndNext}
            className="form-button"
            w="33%"
            loading={loading}
          >
            Save and Proceed
          </PrimaryButton>
          <PrimaryButton
            onClick={() => handlePrevious()}
            className="form-button"
            loading={loading}
            w="33.3%"
          >
            Previous
          </PrimaryButton>
          <PrimaryButton
            onClick={() => onClose()}
            className="form-button"
            loading={loading}
            w="33.3%"
          >
            Close
          </PrimaryButton>
        </div>
      </div>
    )
  }

  const CreditAgreementPreambleContent: React.FC<CustomStepContentProps> = ({
    onNext,
    onPrevious,
    onClose,
  }) => {
    const childRef = useRef<ChildCreditAgreementPreambleRef>(null)
    const [loading, setLoading] = useState(false)

    void onNext // Prevent unused variable warning, this is currently last step in stepper so no need for onNext

    const handleSaveDealAndExit = async () => {
      if (childRef.current) {
        setLoading(true)
        await childRef.current.handleClick()
        setLoading(false)
        onClose()
      }
    }

    const handlePrevious = () => {
      if (typeof onNext === 'function') {
        onPrevious()
      }
    }
    return (
      <div className="create-new">
        <CreditPreambleComponent
          deal={upd_deal}
          amendment={amendment}
          dealAmendment={upd_amendment}
          setAmendment={setAmendment}
          setDeal={setDeal}
          ref={childRef}
        />
        <div
          className="btn-group"
          style={{ marginTop: 10, display: 'flex', gap: '10px' }}
        >
          <PrimaryButton
            onClick={() => handleSaveDealAndExit()}
            className="form-button"
            w="33.3%"
            loading={loading}
          >
            Save and Exit
          </PrimaryButton>
          <PrimaryButton
            onClick={() => handlePrevious()}
            className="form-button"
            loading={loading}
            w="33.3%"
          >
            Previous
          </PrimaryButton>
          <PrimaryButton
            onClick={() => onClose()}
            className="form-button"
            loading={loading}
            w="33.3%"
          >
            Close
          </PrimaryButton>
        </div>
      </div>
    )
  }

  const steps = [
    {
      label: '1. Deal',
      content: (
        <DealContent
          onNext={function (): void {
            throw new Error('Function not implemented.')
          }}
          onPrevious={function (): void {
            throw new Error('Function not implemented.')
          }}
          onClose={onClose}
        />
      ),
    },
    {
      label: 'i. Fees',
      content: (
        <FeesContent
          onNext={function (): void {
            throw new Error('Function not implemented.')
          }}
          onPrevious={function (): void {
            throw new Error('Function not implemented.')
          }}
          onClose={onClose}
        />
      ),
    },
    {
      label: 'ii. Pricing Grid',
      content: (
        <PricingGridContent
          onNext={function (): void {
            throw new Error('Function not implemented.')
          }}
          onPrevious={function (): void {
            throw new Error('Function not implemented.')
          }}
          onClose={onClose}
        />
      ),
    },
    {
      label: 'iii. Assignability Rules',
      content: <DealAssignabilityContent onNext={function (): void {
        throw new Error('Function not implemented.')
      }}
        onPrevious={function (): void {
          throw new Error('Function not implemented.')
        }} onClose={onClose} />,
    },
    {
      label: 'iv. Credit Agreement Preamble',
      content: <CreditAgreementPreambleContent onNext={function (): void {
        throw new Error('Function not implemented.')
      }}
        onPrevious={function (): void {
          throw new Error('Function not implemented.')
        }} onClose={onClose} />,
    }
  ]

  return (
    <>
      {!upd_deal || Object.keys(upd_deal).length === 0 ? (
        //create
        < VerticalStepper
          steps={steps}
          disabledSteps={[1, 2, 3, 4, 5]}
          step={step}
        />
      ) : (
        // edit
        <VerticalStepper
          steps={steps}
          disabledSteps={disabledSteps ? disabledSteps : (upd_deal.isPricingGrid || upd_amendment?.isPricingGrid) ? [] : [2]}
          step={step}
        />
      )}
    </>
  )
}

export default DealsFormWithoutStepper